import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PrimeurCampaign from '../index';
import styled from 'styled-components';
import Card from '../../../components/Card';

import AverageMarginCard from '../components/AverageMarginCard';
import KeyIndicatorsCard from '../components/KeyIndicatorsCard';
import SellerInfoCard from '../components/SellerInfoCard';
import RecapHistoryCard from '../components/RecapHistoryCard';
import RecapFilters from '../components/RecapFilters';
import NumberSellerCard from '../components/NumberSellerCard';

import { getPrimeurRecap } from '../../../actions/primeur';

import Spinner from '../../../components/Spinner';
import { I18n } from 'react-redux-i18n';

const RecapContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 40px;

  .filterContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .filterContainer:not(:last-children) {
    margin-right: 45px;
  }

  .calendarContainer {
    width: 300px;
    margin-right: 45px;
  }

  .filterCard {
    margin: 0px;
    margin-bottom: 20px;
  }

  .recapContentContainer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
    margin-bottom: 20px;
  }

  @media (max-width: 1400px) {
    .recapContentContainer {
      grid-template-columns: 1fr;
    }
  }
`;

const Recap = () => {
  const [wine, setWine] = useState(null);
  const [vintage, setVintage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optionsVintage, setOptionsVintage] = useState([]);

  const primeur = useSelector((state) => state.primeur);
  const dispatch = useDispatch();

  const dispatchGetPrimeurRecap = useCallback(
    async (v) => {
      await dispatch(getPrimeurRecap(v));
    },
    [dispatch]
  );

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      await dispatchGetPrimeurRecap();
      setLoading(false);
    };
    fetchData();
  }, [dispatchGetPrimeurRecap]);

  // Set defaults when data arrives
  useEffect(() => {
    if (primeur?.client_wines?.length > 0 && !wine) {
      const maxVintage = String(
        Math.max(...primeur.client_wines.map((item) => Number(item.vintage)))
      );
      const wineForVintage = primeur.client_wines.find(
        (item) => String(item.vintage) === maxVintage
      );
      const defaultWine = wineForVintage || primeur.client_wines[0];
      setWine({ value: defaultWine.wine, label: defaultWine.wine });
      setVintage({ value: maxVintage, label: maxVintage });
      setOptionsVintage((prev) => [...prev, maxVintage]);
    }
  }, [primeur]);

  const formatOptions = () => {
    const client_wines = primeur.client_wines || [];
    const wineOptions = [...new Set(client_wines.map((item) => item.wine))].map(
      (item) => ({ value: item, label: item })
    );
    let vintageOptions = [];
    if (optionsVintage && optionsVintage.length > 0) {
      vintageOptions = [
        Number(optionsVintage[0]),
        Number(optionsVintage[0]) - 1,
        Number(optionsVintage[0]) - 2
      ].map((item) => ({ value: item, label: item }));
    }
    return { wine: wineOptions, vintage: vintageOptions };
  };

  const filterData = () => {
    const histo_price = primeur.recap?.histo_price || [];
    const client_wines = primeur.client_wines || [];
    const clientWinesNames = client_wines.map((item) => item.wine);

    let recapFiltered = [...histo_price];
    recapFiltered = recapFiltered.filter((item) =>
      clientWinesNames.includes(item.wine_name)
    );
    recapFiltered = wine
      ? recapFiltered.filter((item) => item.wine_name === wine.label)
      : recapFiltered.length > 0
        ? recapFiltered.filter(
            (item) => item.wine_name === recapFiltered[0].wine_name
          )
        : [];
    recapFiltered = vintage
      ? recapFiltered.filter((item) => item.vintage == vintage.value)
      : recapFiltered;
    return recapFiltered;
  };

  const getIndicators = () => {
    const kpis = primeur.recap?.kpis || [];
    const histo_price = primeur.recap?.histo_price || [];
    const client_wines = primeur.client_wines || [];
    const clientWinesNames = client_wines.map((item) => item.wine);

    let recapFiltered = [...histo_price];
    recapFiltered = recapFiltered.filter((item) =>
      clientWinesNames.includes(item.wine_name)
    );

    const tmpKpi = {
      lowest_margin_store: { name: '' },
      total_pmm_alerts: 0,
      max_sellers_bottle: 0,
      max_sellers_all: 0
    };
    let kpiToShow = kpis && kpis.length > 0 ? kpis[0] : tmpKpi;
    if (wine) {
      kpiToShow = kpis.filter((item) => item.wine_name === wine.value);
    } else {
      if (recapFiltered && recapFiltered.length > 0) {
        kpiToShow = kpis.filter(
          (item) => item.wine_name === recapFiltered[0].wine_name
        );
      }
    }
    if (vintage && kpiToShow && kpiToShow.length > 0) {
      kpiToShow = kpiToShow.filter((item) => item.vintage == vintage.value);
    }
    if (kpiToShow && kpiToShow.length > 0) {
      kpiToShow = kpiToShow[0];
    } else {
      kpiToShow = tmpKpi;
    }
    return [
      {
        title: kpiToShow.total_pmm_alerts,
        content: I18n.t('primeur.recap.indicators.pmmAlerts')
      },
      {
        title: kpiToShow.lowest_margin_store_name,
        content: I18n.t('primeur.recap.indicators.lowestStore')
      },
      {
        title: kpiToShow.max_sellers_bottle || 0,
        content: I18n.t('primeur.recap.indicators.maxSeller')
      },
      {
        title: kpiToShow.max_sellers_all,
        content: I18n.t('primeur.recap.indicators.totalSellers')
      }
    ];
  };

  const filterStores = () => {
    const store_stats = primeur.recap?.store_stats || [];
    let filteredStores = [...store_stats];
    filteredStores = wine
      ? filteredStores.filter((item) => item.wine_name === wine.label)
      : filteredStores.length > 0
        ? filteredStores.filter(
            (item) => item.wine_name === filteredStores[0].wine_name
          )
        : [];
    return filteredStores;
  };

  const filterStats = () => {
    const client_wine_stats = primeur.recap?.client_wine_stats || [];
    const rival_wine_stats = primeur.recap?.rival_wine_stats || [];

    let data = [];
    if (wine) {
      data = client_wine_stats.find((item) => item.wine === wine.label);
    }
    if (data) {
      const filteredRivals = rival_wine_stats.filter(
        (item) => item.client_wine === data.wine_id
      );
      return [data].concat(filteredRivals);
    }
    return [];
  };

  const handleChange = async (field, value) => {
    if (field === 'vintage') {
      setLoading(true);
      await dispatchGetPrimeurRecap(value ? value.value : '');
      setVintage(value);
      setLoading(false);
    } else if (field === 'wine') {
      setWine(value);
    }
  };

  const filteredData = filterData();
  const indicators = getIndicators();
  const options = formatOptions();
  const filteredStoreStats = filterStores();
  const filteredWineStats = filterStats();

  return (
    <PrimeurCampaign>
      <RecapContainer>
        <Card className="filterCard">
          <div className="filterContainer">
            <RecapFilters
              selections={{ wine, vintage }}
              options={options}
              onChange={(field, value) => handleChange(field, value)}
            />
          </div>
        </Card>
        <div className="recapContentContainer">
          <RecapHistoryCard data={filteredData} />
          <KeyIndicatorsCard indicators={indicators} />
          <NumberSellerCard data={filteredData} />
          <AverageMarginCard
            retractedSize={5}
            data={filteredWineStats}
            wine={wine}
            vintage={vintage}
          />
        </div>
        <SellerInfoCard
          data={filteredStoreStats}
          retractedSize={10}
          wine={wine}
          vintage={vintage}
        />
      </RecapContainer>

      <Spinner loading={loading} />
    </PrimeurCampaign>
  );
};

export default Recap;
